<template>
  <b-card :class="pointer_style" body-class="p-3">
    <div class="d-flex align-items-center">
      <div class="text-nowrap">
        <span
          class="fas fa-grip-vertical fa-fw"
          :class="grip_symbol_visibility"
        />
        <span class="fas fa-code mx-2" :class="code_symbol_visibility" />
      </div>

      <div class="flex-w-stretch text-truncate">
        <span v-if="!in_edition">
          <span v-c-tooltip="(value || {}).name">
            {{ value.name }}
          </span>
        </span>
        <!--         <b-form-select
          v-else
          v-model="new_column_name"
          :options="column_names_options"
        /> -->
        <template v-else>
          <b-form-input v-if="only_manual" v-model="new_column_name" />
          <b-form-select
            v-else
            v-model="new_column_name"
            :options="column_names_options"
          />
        </template>
      </div>

      <span class="flex-w-stretch">
        <slot name="right_element" v-bind="{ in_edition }" />
      </span>

      <div class="ml-2">
        <b-btn-group v-if="is_new_column_name">
          <b-btn
            v-if="new_column_name !== null"
            variant="outline-secondary"
            @click="validate_edition"
          >
            <span class="fas fa-plus" />
          </b-btn>
          <b-btn variant="outline-secondary" @click="select_all">
            <span class="far fa-layer-plus fa-fw" />
          </b-btn>
          <b-btn variant="outline-secondary" @click="deselect_all">
            <span class="far fa-layer-minus fa-fw" />
          </b-btn>
        </b-btn-group>

        <b-btn-group v-else>
          <b-btn variant="outline-secondary" @click="toggle_edition()">
            <span v-if="in_edition" class="fas fa-check fa-fw" />
            <span v-else class="fas fa-pen fa-fw" />
          </b-btn>
          <b-btn variant="outline-secondary" @click="$emit('trash')">
            <span class="fas fa-trash fa-fw" />
          </b-btn>
        </b-btn-group>
      </div>
    </div>

    <div v-if="new_column_name === null && (in_edition || is_new_column_name)">
      <label>
        <b>{{ $t('column_name_wanted') }}:</b>
      </label>
      <div class="d-flex align-items-center">
        <manual-column-name v-model="manual_generic_column_name" />
        <b-btn-group class="ml-2">
          <b-btn
            v-if="is_new_column_name"
            variant="outline-secondary"
            @click="validate_edition"
          >
            <span class="fas fa-plus fa-fw" />
          </b-btn>
          <b-btn
            v-c-tooltip="$t('reinit_column_name')"
            variant="outline-secondary"
            @click="destructure_value"
          >
            <span class="fas fa-times fa-fw" />
          </b-btn>
        </b-btn-group>
      </div>
    </div>
    <slot name="bottom_element" v-bind="{ in_edition }" />
  </b-card>
</template>

<i18n>
{
  "en": {
    "reinit_column_name": "Reinitialize to default value"
  },
  "fr": {
    "reinit_column_name": "Réinitaliser à la valeur par défaut"
  }
}
</i18n>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import {
  GenericColumnName,
  GenericColumnNameWithRightField,
  GenericColumnNameWithRightAndBottomField,
} from '@/components/object_model.js'
import ManualColumnName from '@/components/manual_column_name.vue'

export default {
  name: 'GenericColumnNameEdition',
  components: {
    draggable,
    ManualColumnName,
  },
  props: [
    'value',
    'available_unused_column_names',
    'available_column_names',
    'is_new_column_name',
    'in_edition',
    'only_manual',
  ],
  data() {
    return {
      new_column_name: '',
      manual_generic_column_name: new GenericColumnName(),
    }
  },

  computed: {
    local_value() {
      let res
      if (this.new_column_name !== '') {
        if (this.new_column_name !== null) {
          res = new GenericColumnName(this.new_column_name, false)
        } else {
          if (this.manual_generic_column_name.name !== '') {
            res = this.manual_generic_column_name
          }
        }
      }
      return res
    },
    column_names_options() {
      let res = [
        { value: '', text: this.$t('choose_a_column') },
        { value: null, text: this.$t('enter_manual_column') },
      ]

      res = res.concat(
        _.map(this.available_unused_column_names, function (elt) {
          return { value: elt, text: elt }
        })
      )

      if (
        this.in_edition &&
        _.indexOf(this.available_column_names, this.value.name) !== -1 &&
        !this.value.contains_metavariables
      ) {
        res.push({ value: this.new_column_name, text: this.new_column_name })
      }

      return res
    },
    grip_symbol_visibility() {
      let res
      if (!this.is_new_column_name) {
        res = 'visible'
      } else {
        res = 'invisible'
      }
      return res
    },
    code_symbol_visibility() {
      let res
      if (!this.in_edition && this.value.contains_metavariables) {
        res = 'visible'
      } else {
        res = 'invisible'
      }
      return res
    },
    pointer_style() {
      let res
      if (!this.is_new_column_name) {
        res = ['col-handle', 'hand-pointer']
      } else {
        res = []
      }
      return res
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.destructure_value()
        }
      },
    },
  },

  methods: {
    destructure_value() {
      if (
        _.indexOf(this.available_column_names, this.value.name) !== -1 &&
        !this.value.contains_metavariables
      ) {
        this.new_column_name = this.value.name
        this.manual_generic_column_name = new GenericColumnName()
      } else {
        this.manual_generic_column_name = new GenericColumnName(
          this.value.name,
          this.value.contains_metavariables
        )
        if (!this.in_edition) {
          this.new_column_name = null
        } else {
          this.new_column_name = ''
        }
      }
    },

    validate_edition() {
      if (
        !(
          this.new_column_name == '' ||
          (this.new_column_name == null &&
            this.manual_generic_column_name.name == '')
        )
      ) {
        this.$emit('input', this.local_value)
      }
    },

    select_all() {
      this.$emit('select_all')
    },
    deselect_all() {
      this.$emit('deselect_all')
    },
    toggle_edition() {
      if (this.in_edition) {
        this.validate_edition()
      } else {
        this.$emit('start_editing')
      }
    },
  },
}
</script>
